import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, ModalWrapper, useAsyncModal } from "@msuite/ui/modal";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import type { ResourceLinkListItem } from "@msuite/utils/types";
import React from "react";
import { useCitadel } from "@/hooks/use-citadel";
import { useProject } from "@/hooks/use-project";
import { client } from "@/hono";
import { EditResourceLinkModal } from "@/modals/projects/edit-resource-link-modal";

export function RowActions({ cell }: CellContext<ResourceLinkListItem, "actions">) {
	/** Hooks */
	const { refetch } = useDataTableContext();
	const { t } = useTranslation();
	const asyncModal = useAsyncModal();
	const editResourceLinkModal = useDisclosure();

	/** Query */
	const { citadel } = useCitadel();
	const { project } = useProject({ projectId: cell.row.original.projectId });

	/** Functions */
	async function handleOnDelete() {
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		const response = await client.projects[":projectId"]["resource-links"][
			":resourceLinkId"
		].$delete({
			param: {
				projectId: cell.row.original.projectId,
				resourceLinkId: cell.row.original.id,
			},
		});
		await responseHandler(response);
		await refetch?.();
	}

	/** Computed */
	// A resource link is a pointer the site keeps and has no subject of its own, so changing one is
	// changing the project — the project this link hangs off, since `update Project` also reaches
	// only the sites its holder runs. Same three-state guard as `Can` documents.
	const updateIsDenied =
		!!citadel && !!project && citadel.cannot("update", "Project", { ...project });

	/** Render */
	return (
		<React.Fragment>
			<RowActionsWrapper>
				{!updateIsDenied && (
					<Menu
						items={createStandardMenuItems({
							onEdit: editResourceLinkModal.onOpen,
							onDelete: handleOnDelete,
						})}
					>
						<Button
							size="xxs"
							icon={<Icon name="EllipsisVertical" />}
							variant="ghost"
							data-cy="resource-link-row-actions-button"
						/>
					</Menu>
				)}
			</RowActionsWrapper>
			<ModalWrapper>
				{asyncModal.isOpen && (
					<ConfirmModal
						controller={asyncModal}
						message={t("confirmation.confirm_deletion")}
					/>
				)}
				{editResourceLinkModal.isOpen && (
					<EditResourceLinkModal
						isOpen={editResourceLinkModal.isOpen}
						onClose={editResourceLinkModal.onClose}
						projectId={cell.row.original.projectId}
						resourceLinkId={cell.row.original.id}
						onComplete={refetch}
					/>
				)}
			</ModalWrapper>
		</React.Fragment>
	);
}
